import { formatCurrency } from '@/lib/formatters';
import type { DiretoriaKpiTone } from '@/lib/diretoriaKpiTone';

export type DiretoriaExecutiveSource = 'mock' | 'api' | 'erp';

export interface DiretoriaExecutiveCard {
  id: string;
  label: string;
  value: string;
  sublabel?: string;
  trend?: string;
  trendPositive?: boolean;
  /** Verde = bom; vermelho = risco / atenção; omitir = neutro. */
  valueTone?: DiretoriaKpiTone;
  source: DiretoriaExecutiveSource;
}

/** Resposta mínima de `/reports/dashboard` usada na Diretoria */
export interface DiretoriaDashboardStats {
  totalTitulos?: number;
  titulosAbertos?: number;
  titulosVencidos?: number;
  clientesInadimplentes?: number;
  valorTotal?: number;
  operadoresAtivos?: number;
  empresasAtivas?: number;
  aging1a2Dias?: { count?: number; valor?: number };
  aging3a6Dias?: { count?: number; valor?: number };
  agingAcima7Dias?: { count?: number; valor?: number };
  titulosPorEmpresa?: Array<{ empresa: string; count: number; valor: number }>;
}

export interface DiretoriaFaturamentoMesStats {
  totalPago: number;
  ano: number;
  mes: number;
  companyId?: string;
  empresaid?: number;
}

export interface DiretoriaReceitaProjetadaStats {
  totalAReceber: number;
  competencias: Array<{ ano: number; mes: number; competencia: string; totalAReceber: number }>;
  companyId?: string;
  empresaid?: number;
}

export function mergeExecutiveWithDashboard(
  mockCards: DiretoriaExecutiveCard[],
  stats: DiretoriaDashboardStats | null | undefined
): DiretoriaExecutiveCard[] {
  if (!stats) return mockCards;

  return mockCards.map((card) => {
    switch (card.id) {
      case 'exec-valor-carteira': {
        const v = Number(stats.valorTotal ?? 0);
        return {
          ...card,
          value: formatCurrency(v),
          sublabel: 'Valor em títulos (dashboard)',
          source: 'api',
          valueTone: v > 0 ? 'bad' : 'neutral',
        };
      }
      case 'exec-titulos-vencidos': {
        const n = Number(stats.titulosVencidos ?? 0);
        return {
          ...card,
          value: String(n),
          sublabel: 'Títulos vencidos / parciais (dashboard)',
          source: 'api',
          valueTone: n === 0 ? 'good' : 'bad',
        };
      }
      case 'exec-clientes-inad': {
        const n = Number(stats.clientesInadimplentes ?? 0);
        return {
          ...card,
          value: String(n),
          sublabel: 'Com títulos vencidos',
          source: 'api',
          valueTone: n === 0 ? 'good' : 'bad',
        };
      }
      case 'exec-aging-leve': {
        const n = Number(stats.aging1a2Dias?.count ?? 0);
        return {
          ...card,
          value: String(n),
          sublabel: 'Títulos vencidos há 1–2 dias',
          source: 'api',
          valueTone: n === 0 ? 'good' : 'bad',
        };
      }
      default:
        return card;
    }
  });
}

function formatMonthYear(mes?: number, ano?: number): string {
  if (!mes || !ano) return 'ERP FlyStart';
  return `${String(mes).padStart(2, '0')}/${ano}`;
}

export function mergeExecutiveWithErpMetrics(
  cards: DiretoriaExecutiveCard[],
  faturamento?: DiretoriaFaturamentoMesStats | null,
  receitaProjetada?: DiretoriaReceitaProjetadaStats | null
): DiretoriaExecutiveCard[] {
  return cards.map((card) => {
    if (card.id === 'exec-faturamento-demo' && faturamento) {
      return {
        ...card,
        label: 'Faturamento (mês)',
        value: formatCurrency(Number(faturamento.totalPago ?? 0)),
        sublabel: `${formatMonthYear(faturamento.mes, faturamento.ano)} · ERP FlyStart`,
        trend: undefined,
        trendPositive: undefined,
        valueTone: 'good',
        source: 'erp',
      };
    }
    if (card.id === 'exec-receita-projetada' && receitaProjetada) {
      const n = receitaProjetada.competencias?.length ?? 0;
      return {
        ...card,
        value: formatCurrency(Number(receitaProjetada.totalAReceber ?? 0)),
        sublabel: n > 0 ? `${n} competência${n === 1 ? '' : 's'} · ERP FlyStart` : 'Hoje em diante · ERP FlyStart',
        valueTone: 'good',
        source: 'erp',
      };
    }
    return card;
  });
}

/** Faixas de atraso: `valor` = saldo em R$; `valor2` = quantidade de títulos (tooltip). */
export function dashboardToAgingChart(stats: DiretoriaDashboardStats | null | undefined) {
  if (!stats) return null;
  return [
    {
      name: '1–2 dias',
      valor: Number(stats.aging1a2Dias?.valor ?? 0),
      valor2: Number(stats.aging1a2Dias?.count ?? 0),
    },
    {
      name: '3–6 dias',
      valor: Number(stats.aging3a6Dias?.valor ?? 0),
      valor2: Number(stats.aging3a6Dias?.count ?? 0),
    },
    {
      name: '7+ dias',
      valor: Number(stats.agingAcima7Dias?.valor ?? 0),
      valor2: Number(stats.agingAcima7Dias?.count ?? 0),
    },
  ];
}

export function dashboardToEmpresaBar(stats: DiretoriaDashboardStats | null | undefined, take = 6) {
  const rows = stats?.titulosPorEmpresa;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return [...rows]
    .sort((a, b) => Number(b.valor ?? 0) - Number(a.valor ?? 0))
    .slice(0, take)
    .map((r) => ({
      name: r.empresa?.length > 18 ? `${r.empresa.slice(0, 16)}…` : r.empresa,
      valor: Number(r.count ?? 0),
      valor2: Number(r.valor ?? 0),
    }));
}
